import { useEffect, useState } from 'react';
import { DELIVERY_FEE_TIER_LABELS } from './constants';
import {
  getCurrentTierIndex,
  getDeliveryTierOverallProgressPercent,
} from './utils';
import cn from 'classnames';
import { Icon } from 'components/common';
import { TruckIcon } from './TruckIcon';
import { DeliveryFeeTierProps } from './DeliveryFeeTier';

interface DeliveryFeeProgressStepperProps {
  total: number;
  previousTotal?: number;
  spaceBetweenStepLabel?: boolean;
  variant?: DeliveryFeeTierProps['variant'];
}

const DeliveryFeeProgressStepper = ({
  total,
  previousTotal,
  spaceBetweenStepLabel,
  variant,
}: DeliveryFeeProgressStepperProps) => {
  const percent = getDeliveryTierOverallProgressPercent(total);
  const previousPercent = getDeliveryTierOverallProgressPercent(
    previousTotal ?? 0,
  );

  const currentTierIndex = getCurrentTierIndex(total);
  const previousIndex = getCurrentTierIndex(previousTotal ?? 0);
  const lastIndex = DELIVERY_FEE_TIER_LABELS.length - 1;

  /**
   * Progress bar has its own animation state.
   * It follows the actual percentage continuously.
   */
  const [animPercent, setAnimPercent] = useState(previousPercent);

  /**
   * Truck has its own position.
   * It only moves when tier/index changes.
   */
  const [truckIndex, setTruckIndex] = useState(previousIndex);
  const [isTruckMoving, setIsTruckMoving] = useState(false);
  const [isTruckLanding, setIsTruckLanding] = useState(false);
  const [truckDirection, setTruckDirection] = useState<
    'forward' | 'backward'
  >('forward');

  /** Progress bar animation */
  useEffect(() => {
    const timeout = setTimeout(() => setAnimPercent(percent), 400);
    return () => clearTimeout(timeout);
  }, [percent]);

  /** Truck animation */
  useEffect(() => {
    if (currentTierIndex === truckIndex) return;

    const direction =
      currentTierIndex > truckIndex ? 'forward' : 'backward';

    setTruckDirection(direction);
    setIsTruckMoving(true);
    setIsTruckLanding(false);

    const movingTimeout = setTimeout(() => {
      setTruckIndex(currentTierIndex);
      setIsTruckMoving(false);
      setIsTruckLanding(true);
    }, 1000);

    const landingTimeout = setTimeout(() => {
      setIsTruckLanding(false);
      setTruckDirection('forward');
    }, 3500);

    return () => {
      clearTimeout(movingTimeout);
      clearTimeout(landingTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTierIndex]);

  const truckPercent = (truckIndex / lastIndex) * 100;

  return (
    <div className="tw-w-full tw-shrink-0">
      {/* Track + dots + floating truck */}
      <div className="tw-relative tw-h-10 tw-mx-6">
        <div className="tw-absolute tw-inset-x-0 tw-top-1/2 -tw-translate-y-1/2 tw-h-2 tw-bg-white-200 tw-rounded-full tw-overflow-hidden">
          <div
            className="tw-h-full tw-bg-primary tw-rounded-full tw-transition-all tw-duration-1000 tw-ease-in-out"
            style={{ width: `${animPercent}%` }}
          />
        </div>
        {DELIVERY_FEE_TIER_LABELS.map((_, index) => {
          const isReached = currentTierIndex >= index;
          const isNext = !isReached && index === currentTierIndex;
          const dotPercent = (index / lastIndex) * 100;

          return (
            <div
              key={index}
              className="tw-absolute tw-top-1/2"
              style={{
                left: `${dotPercent}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div
                className={cn(
                  'tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors tw-duration-300',
                  isReached
                    ? 'tw-bg-primary tw-text-white'
                    : isNext
                    ? 'tw-bg-white tw-border-2 tw-border-primary tw-ring-4 tw-ring-primary/20'
                    : 'tw-bg-white-200',
                )}
              >
                {isReached && (
                  <Icon
                    name="check"
                    fontSize={8}
                    className="tw-leading-none -tw-mt-0.5"
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* Truck */}
        <div
          className="tw-absolute tw-top-1/2 tw-z-10 tw-transition-all tw-duration-1000 tw-ease-in-out"
          style={{
            left: `${truckPercent}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div
            className={cn(
              'tw-w-12 tw-h-12 tw-rounded-full tw-bg-primary tw-shadow-md tw-flex tw-items-center tw-justify-center',
              isTruckMoving && 'tw-animate-truck-drive',
              isTruckLanding && 'tw-animate-pop-burst',
            )}
          >
            <TruckIcon
              className={cn(
                'tw-w-9 tw-h-9 tw-text-white tw-transition-transform tw-duration-500',
                truckDirection === 'backward' && 'tw-scale-x-[-1]',
              )}
            />
          </div>
        </div>
      </div>

      {/* Labels */}
      <div
        className={cn(
          'tw-flex tw-justify-between tw-w-full',
          spaceBetweenStepLabel ? 'tw-mt-3' : 'tw-mt-1.5',
        )}
      >
        {DELIVERY_FEE_TIER_LABELS.map((label, index) => {
          const isPassed = currentTierIndex > index;
          const isNext = !isPassed && index === currentTierIndex;

          return (
            <div
              key={index}
              className={cn(
                variant === 'cart' ? '' : 'tw-text-[1.15rem]',
                index === 0
                  ? 'tw-text-left'
                  : index === lastIndex
                  ? 'tw-text-right'
                  : 'tw-text-center',
                index === lastIndex
                  ? 'tw-font-therebrat tw-font-medium tw-tracking-wide'
                  : 'tw-font-bold',
                isPassed
                  ? 'tw-text-[#999]'
                  : isNext
                  ? 'tw-text-primary'
                  : 'tw-text-[#333]',
              )}
            >
              {label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeliveryFeeProgressStepper;
